/**
 * SkillTreeDefs — SVG <defs>：渐变、glow filter、王冠/锁 symbol
 *
 * 这些 defs 在主图内被 ReactFlow 的 <svg> 节点引用一次（不需要每节点重复）。
 * 放单独文件便于修改和测试。
 */

import { memo } from 'react';
import { CHAIN_COLORS } from '../../utils/chain-constants';

const SkillTreeDefs = memo(function SkillTreeDefs() {
  // 6 种 chain 组合（去重、确定性顺序）
  const combinations: Array<{ key: string; chains: string[] }> = [
    { key: 'n', chains: ['nvidia'] },
    { key: 'g', chains: ['google'] },
    { key: 'h', chains: ['huawei'] },
    { key: 'ng', chains: ['nvidia', 'google'] },
    { key: 'nh', chains: ['nvidia', 'huawei'] },
    { key: 'gh', chains: ['google', 'huawei'] },
    { key: 'ngh', chains: ['nvidia', 'google', 'huawei'] },
  ];

  return (
    <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
      <defs>
        {/* Edge gradients for multi-chain edges */}
        {combinations.map(c => {
          if (c.chains.length === 1) return null;
          const colors = c.chains.map(ch => CHAIN_COLORS[ch] || '#64748b');
          return (
            <linearGradient
              key={`grad-${c.key}`}
              id={`grad-edge-${c.key}`}
              gradientUnits="userSpaceOnUse"
              x1="0" y1="0" x2="100" y2="0"
            >
              {colors.map((color, i) => (
                <stop
                  key={i}
                  offset={`${(i / (colors.length - 1)) * 100}%`}
                  stopColor={color}
                />
              ))}
            </linearGradient>
          );
        })}

        {/* 王冠图标：Toll Station */}
        <symbol id="icon-crown" viewBox="0 0 24 24">
          <path
            d="M3 18 L3 8 L7 13 L12 5 L17 13 L21 8 L21 18 Z"
            fill="#fbbf24"
            stroke="#b45309"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <circle cx="7" cy="6" r="1.4" fill="#fbbf24" stroke="#b45309" strokeWidth="0.8" />
          <circle cx="12" cy="3" r="1.4" fill="#fbbf24" stroke="#b45309" strokeWidth="0.8" />
          <circle cx="17" cy="6" r="1.4" fill="#fbbf24" stroke="#b45309" strokeWidth="0.8" />
        </symbol>

        {/* 锁形图标：不可替代节点（公司 ≤ 2） */}
        <symbol id="icon-lock" viewBox="0 0 24 24">
          <rect x="5" y="11" width="14" height="9" rx="1.5" fill="white" stroke="#dc2626" strokeWidth="1.4" />
          <path d="M8 11 L8 7 a4 4 0 0 1 8 0 L16 11" fill="none" stroke="white" strokeWidth="2" />
        </symbol>

        {/* 投资吸引力填充渐变 */}
        <radialGradient id="fill-gold" cx="35%" cy="35%">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="50%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#b45309" />
        </radialGradient>
        <radialGradient id="fill-blue" cx="35%" cy="35%">
          <stop offset="0%" stopColor="#bfdbfe" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </radialGradient>

        {/* 高瓶颈红色辉光 filter */}
        <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* 收费站金色辉光 */}
        <filter id="glow-toll" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* 箭头 marker：链色循环 */}
        {(['nvidia', 'google', 'huawei'] as const).map(ch => (
          <marker
            key={`arrow-${ch}`}
            id={`arrow-${ch}`}
            viewBox="0 0 8 8"
            refX="7" refY="4"
            markerWidth="6" markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 Z" fill={CHAIN_COLORS[ch] || '#64748b'} />
          </marker>
        ))}
        <marker
          id="arrow-muted"
          viewBox="0 0 8 8"
          refX="7" refY="4"
          markerWidth="5" markerHeight="5"
          orient="auto"
        >
          <path d="M0,0 L8,4 L0,8 Z" fill="#64748b" fillOpacity="0.5" />
        </marker>
      </defs>
    </svg>
  );
});

export default SkillTreeDefs;
